// Reusable validation functions for React Admin forms
// Each returns string (error message) or undefined (valid)

// The reason on suspend / ban / revoke / resubmission / reinstate: jogo-platform
// AdminActionRequest.reason (min_length=10, max_length=1000). Shorter is a 422.
export const ADMIN_ACTION_REASON = { reasonMinLength: 10, reasonMaxLength: 1000 } as const;

export const validateEmail = (value: string) => {
  if (!value) return undefined;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? undefined : 'Must be a valid email address';
};

export const validateUrl = (value: string) => {
  if (!value) return undefined;
  try {
    new URL(value);
    return undefined;
  } catch {
    return 'Must be a valid URL (e.g. https://example.com)';
  }
};

export const validateLatitude = (value: number) => {
  if (value === undefined || value === null) return undefined;
  const num = Number(value);
  if (isNaN(num) || num < -90 || num > 90) {
    return 'Latitude must be between -90 and 90';
  }
  return undefined;
};

export const validateLongitude = (value: number) => {
  if (value === undefined || value === null) return undefined;
  const num = Number(value);
  if (isNaN(num) || num < -180 || num > 180) {
    return 'Longitude must be between -180 and 180';
  }
  return undefined;
};

export const validatePhone = (value: string) => {
  if (!value) return undefined;
  const phoneRegex = /^\+?[\d\s\-()]{7,20}$/;
  return phoneRegex.test(value) ? undefined : 'Must be a valid phone number';
};

export const validatePasswordStrength = (value: string) => {
  if (!value) return undefined;
  if (value.length < 8) return 'Password must be at least 8 characters';
  if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
  if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
  if (!/\d/.test(value)) return 'Password must contain a number';
  return undefined;
};

export const validateDateFormat = (format: string) => (value: string) => {
  if (!value) return undefined;
  if (format === 'dd/mm/yyyy') {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(value)) return 'Must be in dd/mm/yyyy format';
    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day || date.getMonth() !== month - 1) {
      return 'Invalid date';
    }
  } else if (format === 'YYYY-MM-DD HH:MM') {
    const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (!regex.test(value)) return 'Must be in YYYY-MM-DD HH:MM format';
  }
  return undefined;
};

export const validatePositiveNumber = (value: number) => {
  if (value === undefined || value === null) return undefined;
  return Number(value) > 0 ? undefined : 'Must be a positive number';
};

export const validateRange = (min: number, max: number) => (value: number) => {
  if (value === undefined || value === null) return undefined;
  const num = Number(value);
  if (isNaN(num) || num < min || num > max) {
    return `Must be between ${min} and ${max}`;
  }
  return undefined;
};

// Form-level validator for cross-field comparisons (min <= max)
export const validateMinMax = (minField: string, maxField: string, label: string) =>
  (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minVal = values[minField];
    const maxVal = values[maxField];
    if (minVal != null && maxVal != null && Number(minVal) > Number(maxVal)) {
      errors[minField] = `${label}: min must be ≤ max`;
      errors[maxField] = `${label}: max must be ≥ min`;
    }
    return errors;
  };

// Combine multiple form-level validators
export const combineValidators = (...validators: Array<(values: Record<string, any>) => Record<string, string>>) =>
  (values: Record<string, any>) => {
    let errors: Record<string, string> = {};
    for (const validator of validators) {
      errors = { ...errors, ...validator(values) };
    }
    return Object.keys(errors).length ? errors : {};
  };

// Required field helper (React Admin pattern)
export const required = (message = 'Required') => (value: any) =>
  value !== undefined && value !== null && value !== '' ? undefined : message;

// Compose multiple field-level validators
export const composeValidators = (...validators: Array<(value: any) => string | undefined>) =>
  (value: any) => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) return error;
    }
    return undefined;
  };
